import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import InputMask from "react-input-mask";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import usePlans from "../../hooks/usePlans";
import {
	Button,
	CssBaseline,
	TextField,
	Grid,
	Link,
	Typography,
	InputAdornment,
	IconButton,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	CircularProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { trackSignupConversion } from "../../utils/googleAds";
import { Visibility, VisibilityOff, WhatsApp } from "@material-ui/icons";

import WhatsAppIcon from "../../assets/duo-lockup-horizontal-light.svg";
import WhatsAppIconWhite from "../../assets/duo-lockup-horizontal-dark.svg";
import WhatsAppIconBar from "../../assets/duo-lockup-bar.svg";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		minHeight: "100vh",
		overflow: "hidden",
	},

	// --- Painel Esquerdo (Marca) ---
	brandPanel: {
		flex: "0 0 45%",
		background: theme.palette.type === "dark" ? "#0F1E36" : "#2D7BE8",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		padding: theme.spacing(6),
		position: "relative",
		overflow: "hidden",
		[theme.breakpoints.down("sm")]: {
			display: "none",
		},
	},
	decorCircle1: {
		position: "absolute",
		width: 320,
		height: 320,
		borderRadius: "50%",
		background: "rgba(255,255,255,0.06)",
		top: -80,
		right: -80,
	},
	decorCircle2: {
		position: "absolute",
		width: 220,
		height: 220,
		borderRadius: "50%",
		background: "rgba(255,255,255,0.05)",
		bottom: 40,
		left: -60,
	},
	decorCircle3: {
		position: "absolute",
		width: 120,
		height: 120,
		borderRadius: "50%",
		background: "rgba(255,255,255,0.08)",
		bottom: 180,
		right: 40,
	},
	brandContent: {
		position: "relative",
		zIndex: 1,
		textAlign: "center",
		maxWidth: 360,
	},
	brandLogo: {
		height: 72,
		width: "auto",
		marginBottom: theme.spacing(4),
		filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.2))",
	},
	brandTagline: {
		color: "#ffffff",
		fontWeight: 700,
		fontSize: "1.75rem",
		lineHeight: 1.3,
		marginBottom: theme.spacing(2),
	},
	brandSubtitle: {
		color: "rgba(255,255,255,0.75)",
		fontSize: "0.95rem",
		lineHeight: 1.6,
	},
	brandDivider: {
		width: 48,
		height: 3,
		background: "rgba(255,255,255,0.4)",
		borderRadius: 2,
		margin: `${theme.spacing(3)}px auto`,
	},
	brandBadge: {
		display: "inline-block",
		marginTop: theme.spacing(3),
		background: "rgba(255,255,255,0.15)",
		border: "1px solid rgba(255,255,255,0.3)",
		borderRadius: 20,
		padding: `${theme.spacing(0.75)}px ${theme.spacing(2)}px`,
		color: "#ffffff",
		fontSize: "0.85rem",
		fontWeight: 600,
	},

	// --- Painel Direito (Formulário) ---
	formPanel: {
		flex: "0 0 55%",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: theme.spacing(4),
		backgroundColor: theme.palette.login,
		overflowY: "auto",
		[theme.breakpoints.down("sm")]: {
			flex: "0 0 100%",
		},
	},
	formWrapper: {
		width: "100%",
		maxWidth: 400,
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
	},
	mobileLogo: {
		display: "none",
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("sm")]: {
			display: "block",
		},
	},
	mobileLogoImg: {
		height: 48,
		width: "auto",
	},
	formHeader: {
		marginBottom: theme.spacing(3),
	},
	formTitle: {
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(0.5),
	},
	formSubtitle: {
		color: theme.palette.text.secondary,
		fontSize: "0.9rem",
	},
	form: {
		width: "100%",
	},
	submitButton: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
		padding: theme.spacing(1.5),
		fontWeight: 600,
		fontSize: "1rem",
		borderRadius: theme.spacing(1),
		boxShadow: "0px 4px 14px rgba(26, 115, 215, 0.4)",
		textTransform: "none",
	},
	loginRow: {
		marginTop: theme.spacing(1),
		textAlign: "center",
	},
	loginLink: {
		fontWeight: 500,
		fontSize: "0.875rem",
	},
	planMenuItem: {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		padding: "10px 16px",
		position: "relative",
	},
	popularBadge: {
		position: "absolute",
		top: 8,
		right: 8,
		backgroundColor: theme.palette.primary.main,
		color: "white",
		fontSize: 11,
		fontWeight: "bold",
		padding: "2px 6px",
		borderRadius: 12,
	},
	planPrice: {
		fontWeight: 700,
		color: theme.palette.primary.main,
		marginTop: theme.spacing(0.5),
		fontSize: "1.05rem",
	},

	// --- Botão WhatsApp flutuante ---
	whatsappButton: {
		position: "fixed",
		bottom: 24,
		right: 24,
		backgroundColor: "#25D366",
		color: "white",
		borderRadius: "50%",
		width: 56,
		height: 56,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
		zIndex: 1000,
		cursor: "pointer",
		border: "2px solid white",
		transition: "transform 0.2s ease, box-shadow 0.2s ease",
		textDecoration: "none",
		"&:hover": {
			transform: "scale(1.1)",
			boxShadow: "0px 8px 20px rgba(0,0,0,0.3)",
		},
	},
	whatsappBalloon: {
		position: "fixed",
		bottom: 92,
		right: 20,
		backgroundColor: "#075E54",
		color: "white",
		borderRadius: 16,
		padding: "8px 14px",
		fontSize: 13,
		fontWeight: 500,
		boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
		zIndex: 1001,
		maxWidth: 190,
		textAlign: "center",
		animation: "fadeInUp 0.4s ease-out 1s both",
	},
	whatsappBalloonArrow: {
		position: "absolute",
		bottom: -6,
		left: "50%",
		transform: "translateX(-50%)",
		width: 10,
		height: 10,
		backgroundColor: "#075E54",
		clipPath: "polygon(0 0, 100% 0, 50% 100%)",
	},
}));

// --- Schema de Validação ---
const UserSchema = Yup.object().shape({
	name: Yup.string()
		.trim()
		.min(2, "Nome precisa ter ao menos 2 caracteres!")
		.max(100, "Nome não pode conter mais que 100 caracteres!")
		.required("Campo obrigatório"),
	password: Yup.string()
		.min(8, "A senha precisa ter ao menos 8 caracteres!")
		.max(50, "Senha muito grande!")
		.matches(
			/^(?=.*[A-Za-z])(?=.*\d)/,
			"A senha precisa conter letras e números!"
		)
		.required("Campo obrigatório"),
	confirmPassword: Yup.string()
		.oneOf([Yup.ref("password"), null], "As senhas não conferem!")
		.required("Campo obrigatório"),
	email: Yup.string()
		.email("Email inválido")
		.required("Campo obrigatório")
		.matches(/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Email inválido"),
	phone: Yup.string()
		.matches(/^\(\d{2}\) \d{5}-\d{4}$/, "Telefone inválido. Exemplo: (99) 99999-9999")
		.required("Campo obrigatório"),
	planId: Yup.number().required("Selecione um plano"),
});

// --- Input de Telefone ---
const PhoneInput = ({ field, form, ...props }) => (
	<InputMask {...field} mask="(99) 99999-9999" maskChar={null}>
		{(inputProps) => (
			<TextField
				{...inputProps}
				{...props}
				error={Boolean(form.touched[field.name] && form.errors[field.name])}
				helperText={form.touched[field.name] && form.errors[field.name]}
				fullWidth
				variant="outlined"
			/>
		)}
	</InputMask>
);

// --- Input de Senha com visibilidade ---
const PasswordField = ({ field, form, ...props }) => {
	const [showPassword, setShowPassword] = useState(false);
	return (
		<TextField
			{...field}
			{...props}
			type={showPassword ? "text" : "password"}
			fullWidth
			variant="outlined"
			error={Boolean(form.touched[field.name] && form.errors[field.name])}
			helperText={form.touched[field.name] && form.errors[field.name]}
			InputProps={{
				endAdornment: (
					<InputAdornment position="end">
						<IconButton
							aria-label="toggle password visibility"
							onClick={() => setShowPassword((prev) => !prev)}
							edge="end"
						>
							{showPassword ? <VisibilityOff /> : <Visibility />}
						</IconButton>
					</InputAdornment>
				),
			}}
		/>
	);
};

// --- Limpeza de telefone ---
const cleanPhone = (phone) => phone.replace(/\D/g, "");

// --- Componente Principal ---
const SignUp = () => {
	const classes = useStyles();
	const theme = useTheme();
	const isDarkMode = theme.palette.type === "dark";
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const [plans, setPlans] = useState([]);
	const { list: listPlans } = usePlans();

	useEffect(() => {
		const fetchPlans = async () => {
			try {
				const list = await listPlans();
				setPlans(list || []);
			} catch (err) {
				toastError(err);
			}
		};
		fetchPlans();
	}, [listPlans]);

	const handleSignUp = async (values) => {
		try {
			setLoading(true);
			// Somente os campos que o cadastro precisa. Trial, vencimento e
			// status são definidos pelo servidor.
			const cleanedValues = {
				name: values.name.trim(),
				email: values.email.trim().toLowerCase(),
				phone: cleanPhone(values.phone),
				password: values.password,
				planId: values.planId,
			};

			await openApi.post("/companies/cadastro", cleanedValues);

			trackSignupConversion();

			// Notificação externa (CRM): falha aqui não pode quebrar o cadastro.
			try {
				await fetch("https://hook.us2.make.com/xbsptar620e1j7ue7vi67visck901ojs", {
					method: "POST",
					mode: "no-cors",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						name: cleanedValues.name,
						email: cleanedValues.email,
						phone: values.phone,
						planId: values.planId,
						date: new Date().toISOString(),
					}),
				});
			} catch (webhookErr) {
				// silencioso: o cadastro já foi concluído com sucesso
			}

			toast.success(i18n.t("signup.toasts.success"));
			history.push("/login");
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={classes.root}>
			<CssBaseline />

			{/* Painel Esquerdo — Marca */}
			<div className={classes.brandPanel}>
				<div className={classes.decorCircle1} />
				<div className={classes.decorCircle2} />
				<div className={classes.decorCircle3} />

				<div className={classes.brandContent}>
					<img
						src={isDarkMode ? WhatsAppIconWhite : WhatsAppIconBar}
						alt="Message Express"
						className={classes.brandLogo}
					/>

					<div className={classes.brandDivider} />

					<Typography variant="h5" className={classes.brandTagline}>
						Atendimento inteligente, resultados reais.
					</Typography>

					<Typography variant="body2" className={classes.brandSubtitle}>
						Crie sua conta e comece a transformar o atendimento do seu negócio hoje mesmo.
					</Typography>

					<span className={classes.brandBadge}>
						7 dias grátis · Sem cartão de crédito
					</span>
				</div>
			</div>

			{/* Painel Direito — Formulário */}
			<div className={classes.formPanel}>
				<div className={classes.formWrapper}>

					{/* Logo apenas no mobile */}
					<div className={classes.mobileLogo}>
						<img
							src={isDarkMode ? WhatsAppIconWhite : WhatsAppIcon}
							alt="Message Express"
							className={classes.mobileLogoImg}
						/>
					</div>

					<div className={classes.formHeader}>
						<Typography variant="h5" className={classes.formTitle}>
							Crie sua conta
						</Typography>
						<Typography variant="body2" className={classes.formSubtitle}>
							Preencha os dados abaixo para começar
						</Typography>
					</div>

					<Formik
						initialValues={{ name: "", email: "", phone: "", password: "", confirmPassword: "", planId: "" }}
						validationSchema={UserSchema}
						onSubmit={async (values, actions) => {
							await handleSignUp(values);
							actions.setSubmitting(false);
						}}
					>
						{({ isSubmitting, touched, errors }) => (
							<Form className={classes.form}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Field
											as={TextField}
											autoComplete="name"
											name="name"
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											fullWidth
											id="name"
											label="Nome do seu Negócio"
											autoFocus
										/>
									</Grid>
									<Grid item xs={12}>
										<Field
											as={TextField}
											variant="outlined"
											fullWidth
											id="email"
											label={i18n.t("signup.form.email")}
											name="email"
											error={touched.email && Boolean(errors.email)}
											helperText={touched.email && errors.email}
											autoComplete="email"
											required
										/>
									</Grid>
									<Grid item xs={12}>
										<Field
											component={PhoneInput}
											name="phone"
											label="Telefone com DDD"
											required
										/>
									</Grid>
									<Grid item xs={12}>
										<Field
											component={PasswordField}
											name="password"
											label={i18n.t("signup.form.password")}
											required
										/>
									</Grid>
									<Grid item xs={12}>
										<Field
											component={PasswordField}
											name="confirmPassword"
											label="Confirme sua senha"
											required
										/>
									</Grid>
									<Grid item xs={12}>
										<FormControl variant="outlined" fullWidth required>
											<InputLabel id="plan-selection-label">Plano</InputLabel>
											<Field
												as={Select}
												labelId="plan-selection-label"
												id="plan-selection"
												name="planId"
												label="Plano"
												MenuProps={{
													PaperProps: { style: { maxHeight: 400 } },
													anchorOrigin: { vertical: "bottom", horizontal: "left" },
													transformOrigin: { vertical: "top", horizontal: "left" },
													getContentAnchorEl: null,
												}}
											>
												{plans.map((plan) => (
													<MenuItem key={plan.id} value={plan.id} className={classes.planMenuItem}>
														{plan.id === 4 && (
															<span className={classes.popularBadge}>Mais Popular</span>
														)}
														<Typography variant="subtitle2" style={{ fontWeight: 600 }}>
															{plan.name}
														</Typography>
														<Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
															Atendentes: <strong>{plan.users}</strong> · WhatsApp: <strong>{plan.connections}</strong>
														</Typography>
														<Typography variant="body2" className={classes.planPrice}>
															R$ {parseFloat(plan.value).toFixed(2).replace(".", ",")}
														</Typography>
													</MenuItem>
												))}
											</Field>
										</FormControl>
									</Grid>
								</Grid>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									color="primary"
									className={classes.submitButton}
									disabled={isSubmitting || loading}
									startIcon={
										(isSubmitting || loading) && (
											<CircularProgress size={20} color="inherit" />
										)
									}
								>
									{isSubmitting || loading
										? i18n.t("signup.buttons.loading")
										: i18n.t("signup.buttons.submit")}
								</Button>

								<Grid container justifyContent="center" className={classes.loginRow}>
									<Grid item>
										<Link
											component={RouterLink}
											to="/login"
											variant="body2"
											className={classes.loginLink}
										>
											{i18n.t("signup.buttons.login")}
										</Link>
									</Grid>
								</Grid>
							</Form>
						)}
					</Formik>
				</div>
			</div>

			{/* Balão de suporte */}
			<div className={classes.whatsappBalloon}>
				<span>Dúvidas? Clique e fale com nosso suporte!</span>
				<div className={classes.whatsappBalloonArrow} />
			</div>

			{/* Botão flutuante do WhatsApp */}
			<a
				href="https://wa.me/5541988977954?text=Olá,%20estou%20com%20dúvida%20no%20cadastro!"
				target="_blank"
				rel="noopener noreferrer"
				className={classes.whatsappButton}
			>
				<WhatsApp style={{ fontSize: 30 }} />
			</a>

			<style>{`
				@keyframes fadeInUp {
					from { opacity: 0; transform: translateY(10px); }
					to   { opacity: 1; transform: translateY(0); }
				}
			`}</style>
		</div>
	);
};

export default SignUp;
